import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Box,
  Paper,
  CircularProgress,
  Snackbar
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

const InterventionStrategyGenerator = () => {
  const [targetBehavior, setTargetBehavior] = useState('');
  const [clientCharacteristics, setClientCharacteristics] = useState('');
  const [environmentalFactors, setEnvironmentalFactors] = useState('');
  const [previousInterventions, setPreviousInterventions] = useState('');
  const [generatedStrategies, setGeneratedStrategies] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const generateStrategies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/generateInterventionStrategies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetBehavior,
          clientCharacteristics: clientCharacteristics.trim() || "Not provided",
          environmentalFactors: environmentalFactors.trim() || "Not provided",
          previousInterventions: previousInterventions.trim() || "Not provided",
        }),
      });
      
      const responseText = await response.text();
    //   console.log('Full response:', responseText);
  
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }
  
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate strategies');
      }
      setGeneratedStrategies(data.strategies);
    } catch (error) {
      console.error('Error generating strategies:', error);
      setError(`Failed to generate strategies: ${error.message}`);
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedStrategies).then(() => {
      setSnackbarOpen(true);
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Target Behavior or Skill (Required)"
            value={targetBehavior}
            onChange={(e) => setTargetBehavior(e.target.value)}
            required
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Client Characteristics (Optional: age, diagnosis, current skill level)"
            value={clientCharacteristics}
            onChange={(e) => setClientCharacteristics(e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Environmental Factors (Optional)"
            value={environmentalFactors}
            onChange={(e) => setEnvironmentalFactors(e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Previous Interventions Tried (Optional)"
            value={previousInterventions}
            onChange={(e) => setPreviousInterventions(e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={generateStrategies}
            disabled={loading || !targetBehavior}
            fullWidth
          >
            Generate ABA Intervention Strategies
          </Button>
        </Grid>
      </Grid>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>}
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      {generatedStrategies && (
        <Card elevation={2} sx={{ mt: 4, backgroundColor: 'white' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Generated ABA Intervention Strategies:</Typography>
            <ReactMarkdown>{generatedStrategies}</ReactMarkdown>
            <Button
              variant="contained"
              color="secondary"
              onClick={copyToClipboard}
              sx={{ mt: 2 }}
            >
              Copy to Clipboard
            </Button>
          </CardContent>
        </Card>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Copied to clipboard"
      />
    </Paper>
  );
};

export default InterventionStrategyGenerator;